import React, { useState } from 'react'
import { css } from '@emotion/css'
import { FormattedMessage } from 'react-intl'
import { Row, Box, FormValidator } from '../../legacy-design-system/form'
import { Textarea } from '../../legacy-design-system/form-text'
import { theme } from '../../styled'
import { logSuccess, logError } from '../toaster'
import { UpdateEmailDomainsMutation } from './update-email-domains-mutation'
import { Button } from '@contrast-security-inc/design-system-buttons'

function HelpText({ children }) {
  return (
    <p
      className={css`
        max-width: 260px;
        overflow-wrap: normal;
        white-space: normal;
        font-weight: ${theme.fontWeights.primary.regular};
      `}
    >
      {children}
    </p>
  )
}

export const textAreaTestID = 'text-area-test-ID'
export const saveButtonTestID = 'save-button-test-ID'

const DOMAIN_NAME_REGEX =
  /(?:[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?\.)+[a-z0-9][a-z0-9-]{0,61}[a-z0-9]/

export function EmailDomainsForm({
  organizationID,
  emailDomains,
  testFormIsDirty
}: {
  readonly organizationID: string
  readonly emailDomains?: string
  readonly testFormIsDirty?: boolean
}) {
  const [domains, setDomains] = useState(emailDomains || '')
  const [formIsDirty, setFormIsDirty] = useState(testFormIsDirty || false)

  const getErrorMessage =
    (selectedDomains, errorMessage) =>
    ({ validity }) => {
      const domainsTrimmed = selectedDomains.replace(' ', '').trim().split(',')

      return validity.valid &&
        domainsTrimmed.every((domain) =>
          domain ? DOMAIN_NAME_REGEX.test(domain) : true
        ) &&
        /* We don't support domain wild cards so we want to fail loudly when they are entered */
        domainsTrimmed.every(
          (domain) => !domain.includes('*') && !domain.includes('?')
        )
        ? undefined
        : errorMessage
    }

  return (
    <UpdateEmailDomainsMutation>
      {(updateEmailDomains) => (
        <FormValidator value={{ domains }}>
          {({ formIsValid, getFormFieldProps }) => (
            <>
              <div
                className={css`
                  margin-top: 20px;
                `}
              >
                <FormattedMessage
                  id="title_emailDomains"
                  children={(label) => (
                    <Row>
                      <Box basis={8}>
                        <div>
                          <FormattedMessage
                            id="detail_securitySettingsEmailDomains_enterValidDomains"
                            children={(errorMsg) => (
                              <Textarea
                                {...getFormFieldProps({
                                  id: 'domains',
                                  getErrorMessage: getErrorMessage(
                                    domains,
                                    errorMsg
                                  ),
                                  validator: ({ validity }) => {
                                    if (!domains) {
                                      return true
                                    }

                                    return (
                                      getErrorMessage(
                                        domains,
                                        errorMsg
                                      )({
                                        validity
                                      }) === undefined
                                    )
                                  }
                                })}
                                data-testid={textAreaTestID}
                                label={label as any}
                                value={domains}
                                errorMessage={
                                  formIsValid ? '' : (errorMsg as any)
                                }
                                placeholder="contrastsecurity.com, app.contrastsecurity.com"
                                renderHelpContent={() => (
                                  <>
                                    <FormattedMessage
                                      id="title_emailDomains"
                                      children={(msg) => (
                                        <p
                                          className={css`
                                            font-weight: ${theme.fontWeights
                                              .primary.bold};
                                          `}
                                        >
                                          {msg}
                                        </p>
                                      )}
                                    />
                                    <FormattedMessage
                                      id="detail_securitySettingsEmailDomains_tooltip"
                                      children={(msg) => (
                                        <HelpText>{msg}</HelpText>
                                      )}
                                    />
                                  </>
                                )}
                                onChange={(value) => {
                                  setFormIsDirty(true)
                                  setDomains(value)
                                }}
                                expand
                                resize="both"
                              />
                            )}
                          />
                        </div>
                      </Box>
                    </Row>
                  )}
                />
              </div>
              <div
                className={css`
                  display: flex;
                  width: 100%;
                  justify-content: flex-end;
                `}
              >
                <FormattedMessage
                  id="action_save"
                  children={(saveMsg) => (
                    <FormattedMessage
                      id="detail_securitySettingsEmailDomains_errorUpdatingEmailDomains"
                      children={(errorMsg) => (
                        <FormattedMessage
                          id="detail_securitySettingsEmailDomains_successfullyUpdatedDomains"
                          children={(successMsg) => (
                            <Button
                              data-testid={saveButtonTestID}
                              onClick={async () => {
                                try {
                                  await updateEmailDomains({
                                    organizationID,
                                    domains
                                  })
                                  logSuccess(successMsg as any)
                                  setFormIsDirty(false)
                                } catch (error) {
                                  logError(errorMsg as any)
                                }
                              }}
                              isDisabled={!formIsValid || !formIsDirty}
                            >
                              {saveMsg}
                            </Button>
                          )}
                        />
                      )}
                    />
                  )}
                />
              </div>
            </>
          )}
        </FormValidator>
      )}
    </UpdateEmailDomainsMutation>
  )
}
